// useCouncilDeliberation.js — E4-WS2-T01 (refactor-deepening / F4, CouncilView state machine).
//
// Deep hook: the COUNCIL DELIBERATION state machine that was inline in CouncilView — the
// per-agent opinion/rebuttal/error/ranking/synthesis state, the phase transitions, the
// terminal outcome (independent of phase), per-agent progress and the wiring of
// lib/councilClient.runCouncil's streaming callbacks into that state (convoke/cancel/reset).
// Concentrated behind a small interface so CouncilView keeps only the selection state +
// roster derivations + render.
//
// v2: named phases (opinions/review/rebuttal/synthesis) with legacy numeric compatibility;
// terminal status (complete|partial|cancelled|error) is orthogonal to phase; unsaved
// persistence is surfaced; costs stay truthful (unknown != free); a generation guard makes
// late callbacks inert after cancel/reset/unmount.

import { useEffect, useMemo, useRef, useState } from 'react';
import { runCouncil } from '../../lib/councilClient.js';
import { serializeCouncilPrompt } from './councilAttachments.js';
import { rankingsToScores } from './councilModel.js';
import { normalizeTerminal, normalizePhase } from '../../lib/councilProtocol.js';

// Map runCouncil's streaming callbacks → the deliberation state setters (module-level so
// the hook stays small). v2 carries the phase as a NAMED string in `phase`; v1 carries a
// legacy number. normalizing through councilProtocol keeps both working.
function buildDeliberationCallbacks(set) {
  const phaseOf = (evt) => normalizePhase(evt);
  return {
    onStart: ({ protocol_version, mode }) => {
      if (mode) set.mode(mode);
      if (protocol_version) set.protocolVersion(protocol_version);
    },
    onPhaseStart: (evt) => {
      const name = phaseOf(evt);
      if (name) set.phase(name);
      if (evt.chairman) set.effectiveChairmanId(evt.chairman);
      // Record per-agent progress for the phase we just entered.
      if (name && Array.isArray(evt.agents)) set.phaseAgents(name, evt.agents);
    },
    onChunk: (evt) => {
      const name = phaseOf(evt);
      const { agent_id, chunk } = evt;
      if (name === 'opinions') set.opinions((prev) => ({ ...prev, [agent_id]: (prev[agent_id] || '') + chunk }));
      else if (name === 'rebuttal') set.rebuttals((prev) => ({ ...prev, [agent_id]: (prev[agent_id] || '') + chunk }));
      else if (name === 'synthesis') set.synthesis((prev) => prev + chunk);
    },
    onStreamDone: (evt) => {
      const name = phaseOf(evt);
      set.streamDone(name, evt.agent_id, { tokens_in: evt.tokens_in, tokens_out: evt.tokens_out });
    },
    onPhaseComplete: (evt) => {
      const name = phaseOf(evt);
      const { results, label_mapping, result } = evt;
      if (name === 'opinions' && Array.isArray(results)) set.opinionsFromResults(results);
      else if (name === 'review') { set.rankings(results || []); set.labelMapping(label_mapping || {}); }
      else if (name === 'rebuttal' && Array.isArray(results)) set.rebuttalsFromResults(results);
      else if (name === 'synthesis' && result) {
        if (result.agent_id) set.effectiveChairmanId(result.agent_id);
        set.synthesis(result.response || '');
      }
    },
    onError: ({ code, agent_id, message }) => {
      if (code === 'unsaved') set.saved(false);
      if (agent_id) set.agentErrors((prev) => ({ ...prev, [agent_id]: message || code }));
      // `unsaved` is informational (persistence), not a run failure — keep it in a
      // dedicated banner instead of the global error slot.
      else if (code !== 'unsaved') set.errorMessage(`${code}: ${message || ''}`);
    },
    onDone: (payload) => {
      const terminal = normalizeTerminal(payload);
      set.terminal(terminal);
      set.saved(terminal.saved);
      set.phase('done');
      if (terminal.status === 'error' && !terminal.cancelled) {
        set.errorMessage((prev) => prev || 'Deliberação falhou');
      }
    },
  };
}

function clearDeliberationState(set) {
  set.opinions({});
  set.rebuttals({});
  set.agentErrors({});
  set.rankings([]);
  set.labelMapping({});
  set.synthesis('');
  set.requestedChairmanId(null);
  set.effectiveChairmanId(null);
  set.errorMessage('');
  set.terminal(null);
  set.saved(true);
  set.streamStats({});
  set.phaseAgentsCleared?.();
}

// Map a phase to the state slice that holds its per-agent output text.
function producedFor(phase, { opinionsByAgent, rebuttalsByAgent }) {
  if (phase === 'opinions') return opinionsByAgent;
  if (phase === 'rebuttal') return rebuttalsByAgent;
  return {};
}

/**
 * Per-agent progress for the fan-out phases (opinions/rebuttal): the expected
 * agent ids plus whether output / an error was seen for each.
 */
function buildProgress(phaseAgentsMap, opinionsByAgent, rebuttalsByAgent, agentErrors, streamStats) {
  const active = {};
  for (const [phase, agents] of Object.entries(phaseAgentsMap)) {
    const produced = producedFor(phase, { opinionsByAgent, rebuttalsByAgent });
    active[phase] = (agents || []).map((id) => ({
      agentId: id,
      done: streamStats[id]?.phase === phase && streamStats[id]?.done === true,
      errored: !!agentErrors[id],
      chars: (produced[id] || '').length,
    }));
  }
  return active;
}

/** Fold phase:complete results into the {agentId: response} slice. */
function mergeResults(prev, results) {
  const next = { ...prev };
  for (const r of results) if (r?.agent_id && typeof r.response === 'string') next[r.agent_id] = r.response;
  return next;
}

/** The shared setter bundle the callbacks + clear/reset helpers write through. */
function buildSetters(s) {
  return {
    phase: s.setPhase,
    opinions: s.setOpinions,
    opinionsFromResults: (results) => s.setOpinions((prev) => mergeResults(prev, results)),
    rebuttals: s.setRebuttals,
    rebuttalsFromResults: (results) => s.setRebuttals((prev) => mergeResults(prev, results)),
    synthesis: s.setSynthesis,
    requestedChairmanId: s.setRequestedChairmanId,
    effectiveChairmanId: s.setEffectiveChairmanId,
    rankings: s.setRankings,
    labelMapping: s.setLabelMapping,
    agentErrors: s.setAgentErrors,
    errorMessage: s.setErrorMessage,
    mode: s.setMode,
    protocolVersion: s.setProtocolVersion,
    terminal: s.setTerminal,
    saved: s.setSaved,
    streamStats: s.setStreamStats,
    phaseAgents: (ph, agents) => s.setPhaseAgentsMap((prev) => ({ ...prev, [ph]: agents })),
    phaseAgentsCleared: () => s.setPhaseAgentsMap({}),
    streamDone: (ph, agentId, usage) => s.setStreamStats((prev) => ({
      ...prev,
      [agentId]: { ...(prev[agentId] || {}), ...usage, done: true, phase: ph },
    })),
  };
}

export function useCouncilDeliberation() {
  const [phase, setPhase] = useState('idle');
  // Sprint v11+ Super-S #1 — real backend state (v2-aware).
  const [opinionsByAgent, setOpinions] = useState({}); // {agentId: text}
  const [rebuttalsByAgent, setRebuttals] = useState({}); // {agentId: text}
  const [agentErrors, setAgentErrors] = useState({}); // {agentId: 'reason'}
  const [rankings, setRankings] = useState([]); // [{agent_id, parsed_ranking, ranking}]
  const [labelMapping, setLabelMapping] = useState({}); // {A: agentId}
  const [synthesis, setSynthesis] = useState('');
  const [requestedChairmanId, setRequestedChairmanId] = useState(null);
  const [effectiveChairmanId, setEffectiveChairmanId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  // v2 additions.
  const [mode, setMode] = useState('rebuttal');
  const [protocolVersion, setProtocolVersion] = useState(2);
  const [terminal, setTerminal] = useState(null); // normalized council:done
  const [saved, setSaved] = useState(true);
  const [phaseAgentsMap, setPhaseAgentsMap] = useState({}); // {phase: string[]}
  const [streamStats, setStreamStats] = useState({}); // {agentId: {tokens_in, tokens_out}}
  const abortRef = useRef(null);
  const generationRef = useRef(0);

  const progress = useMemo(
    () => buildProgress(phaseAgentsMap, opinionsByAgent, rebuttalsByAgent, agentErrors, streamStats),
    [phaseAgentsMap, opinionsByAgent, rebuttalsByAgent, agentErrors, streamStats],
  );

  const setters = buildSetters({
    setPhase, setOpinions, setRebuttals, setSynthesis, setRequestedChairmanId,
    setEffectiveChairmanId, setRankings, setLabelMapping, setAgentErrors,
    setErrorMessage, setMode, setProtocolVersion, setTerminal, setSaved,
    setStreamStats, setPhaseAgentsMap,
  });

  // Cancel any in-flight run on unmount.
  useEffect(() => () => { generationRef.current += 1; abortRef.current?.(); }, []);

  const scoresByAuthor = useMemo(() => rankingsToScores(rankings, labelMapping), [rankings, labelMapping]);

  function convoke({ query, agents, chairman, attachments, mode: requestedMode }) {
    if (!query.trim() || agents.length < 2 || !chairman) return;
    const generation = ++generationRef.current;
    abortRef.current?.();
    abortRef.current = null;
    // Clear previous deliberation state.
    clearDeliberationState(setters);
    setRequestedChairmanId(chairman);
    const effectiveMode = requestedMode === 'essential' ? 'essential' : 'rebuttal';
    setMode(effectiveMode);
    setProtocolVersion(2);
    setPhase('opinions');

    // Inclui conteúdo dos arquivos texto inline no prompt (blocos <file>), com
    // escape de nome de arquivo e truncamento visível (Council-local).
    const effectiveQuery = serializeCouncilPrompt(query.trim(), attachments);

    let run;
    try {
      run = runCouncil(
        { query: effectiveQuery, agents, chairman, enableReview: true, mode: effectiveMode, protocolVersion: 2 },
        Object.fromEntries(Object.entries(buildDeliberationCallbacks(setters)).map(([name, callback]) => [name, (...args) => {
          if (generationRef.current === generation) callback(...args);
        }])),
      );
    } catch (e) {
      setErrorMessage(e.message);
      setPhase('idle');
      return;
    }
    abortRef.current = run.abort;
  }

  function cancel() {
    generationRef.current += 1;
    abortRef.current?.();
    abortRef.current = null;
    setTerminal((prev) => prev || normalizeTerminal({ status: 'cancelled' }));
    setPhase('done');
  }

  function reset() {
    generationRef.current += 1;
    abortRef.current?.();
    abortRef.current = null;
    clearDeliberationState(setters);
    setPhase('idle');
  }

  const isRunning = phase !== 'idle' && phase !== 'done';

  return {
    phase,
    isRunning,
    opinionsByAgent,
    rebuttalsByAgent,
    agentErrors,
    rankings,
    labelMapping,
    synthesis,
    requestedChairmanId,
    effectiveChairmanId,
    errorMessage,
    mode,
    protocolVersion,
    terminal,
    saved,
    streamStats,
    progress,
    scoresByAuthor,
    convoke,
    cancel,
    reset,
  };
}
